import type { ReactNode } from 'react';

/** Which edge of what it explains the bubble lines up with, so a tile at a side keeps it on screen. */
export type LadoGlobo = 'izquierda' | 'centro' | 'derecha';

const LADO: Record<LadoGlobo, string> = {
  izquierda: 'left-0',
  centro: 'left-1/2 -translate-x-1/2',
  derecha: 'right-0',
};

/**
 * Lo que hace un botón, en un globo encima de él al pasarle el mouse.
 *
 * For a computer with a mouse, which is where the owner asked for it (2026-10-02). A phone has
 * nothing to hover with, and a tap that showed a bubble would have to stop opening the screen, so
 * a phone shows nothing and works as it did: Tailwind's `hover` only applies where the device can
 * hover. From the keyboard it shows on focus, which is the hover of somebody without a mouse.
 *
 * The bubble is the button's sibling, in a group that holds both, not its child: inside, the
 * sentence would become part of the button's name, and a screen reader would read «Nuevo gasto»
 * as the whole explanation. The button names it with `aria-describedby` instead (`id`). It waits
 * a moment before it appears, so a mouse crossing the tiles on its way somewhere else does not
 * light up each one it passes, and it lets clicks through to whatever it covers.
 */
export function ConGlobo({
  id,
  texto,
  lado = 'centro',
  children,
}: {
  /** What the button's `aria-describedby` says. */
  id: string;
  texto: string;
  lado?: LadoGlobo;
  children: ReactNode;
}) {
  return (
    <div className="group/globo relative">
      {children}
      <span
        role="tooltip"
        id={id}
        className={`pointer-events-none invisible absolute bottom-full z-30 mb-2 w-max max-w-72 rounded-xl bg-slate-800 px-3 py-2 text-left text-xs leading-snug text-slate-100 opacity-0 shadow-lg shadow-black/40 ring-1 ring-white/10 transition-[opacity,visibility] duration-150 group-hover/globo:visible group-hover/globo:opacity-100 group-hover/globo:delay-400 group-has-[:focus-visible]/globo:visible group-has-[:focus-visible]/globo:opacity-100 ${LADO[lado]}`}
      >
        {texto}
      </span>
    </div>
  );
}
